<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="90vh")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('create_followup_appointment') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

        v-form(ref="form" v-else-if="appointment")
          div.text-sm.font-semibold.neutral500--text {{ $t('patient_details') }}

          v-card.mt-4.pa-4.round-lg(outlined)
            v-row(dense)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('nric_passport') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.ic || '-' }}
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text {{ $t('last_service') }}
                div.text-sm.neutral700--text.mt-1(v-if="appointment.services.length") {{ appointment.services.map(m => m.name).join(', ') }}
                div.text-sm.neutral700--text.mt-1(v-else) -
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text {{ $t('last_appointment_date') }}
                div.text-sm.neutral700--text.mt-1 {{ $options.filters.humanizeDate(appointment.date) || '-' }}

          div.text-md.font-semibold.neutral700--text.mt-6 {{ $t('choose_appointment_details') }}

          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('service_type') }}
          y-select.mt-2(v-model="form.service" :items="services" item-text="name" item-value="id" :placeholder="$t('select_service_type')" :rules="rules.not_empty_select" :loading="isFetchingService" @change="fetchProviders(form.service)")
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('doctor') }}
          y-select.mt-2(v-model="form.provider" :items="providers" item-text="name" item-value="id" :placeholder="$t('select_doctor')" :rules="rules.not_empty_select" :loading="isFetchingProvider" no-data-text="No provider available" @change="clearDateTime")
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('appointment_date') }}
          y-dialog(v-model='showPicker' width='380px' content-class="round-3xl")
            template(v-slot:activator='{ on }')
              v-text-field.text-sm.mt-2.round-xl(v-model="selectedDateField" :placeholder="$t('select_appointment_date')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto" v-on="on" :disabled="!form.provider || !form.service" :loading="isFetchingDates")
                template(v-slot:append)
                  eva-icon.mt-1(name="calendar-outline" fill="#848484")
            v-card
              v-card-text.px-2.pt-2.pb-0
                div.d-flex.justify-center.align-center(v-if="isFetchingDates" style="height: 380px;")
                  v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
                yc-date-picker(v-else :parentDate.sync="selectedDate" color="info" :allowedDates="unavailableDateList" :minDate="getTodayDate()" :parentPickerDate.sync="pickerDate")
              v-card-actions.px-4.pb-4
                v-row(no-gutters)
                  v-col.pr-2
                    v-btn.button-sm(color="neutral700" block dark outlined depressed @click="closeDate") {{ $t('cancel') }}
                  v-col.pl-2
                    v-btn.button-sm(color="neutral700" block depressed :dark="Boolean(selectedDate)" :disabled="!selectedDate" @click="pickDate(form.service, form.provider, selectedDate)") {{ $t('select') }}

          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('available_time') }}
          y-select.mt-2(v-model="form.time" :items="times" item-text="text" item-value="value" :placeholder="$t('select_available_time')" :rules="rules.not_empty_select" :loading="isFetchingTimes" no-data-text="No timeslot available")
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('notes') }}
          v-textarea.text-sm.mt-2.round-xl(v-model="form.remarks" :placeholder="$t('notes_optional')" rows="3" outlined hide-details="auto")

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        y-button(v-if="showOpenAdvance !== undefined" label="Advanced appointment" underline button-class="px-0" @click="openAdvance()")
        v-btn.button-md.neutral700--text.px-0(v-else @click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="confirmAction") {{ $t('confirm') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'FollowUpAppointment',
  components: {},
  props: ['open', 'selectedAppointment', 'showOpenAdvance'],
  data () {
    return {
      isFetching: false,
      isFetchingService: false,
      isFetchingProvider: false,
      isFetchingDates: false,
      isFetchingTimes: false,
      isLoading: false,
      showPicker: false,
      appointment: null,
      selectedDate: null,
      selectedDateField: null,
      pickerDate: null,
      form: {},
      services: [],
      providers: [],
      unavailableDates: [],
      times: [],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    }
  },
  watch: {
    pickerDate (val) {
      this.fetchUnavailableDates(val)
    }
  },
  mounted () {
    this.fetchAppointment()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointment () {
      this.isFetching = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
          this.$set(this.form, 'service', this.appointment.service.id)
          this.fetchProviders(this.appointment.service.id)
          this.fetchServices()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchServices () {
      this.isFetchingService = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/services?page_size=-1&fields=id,name&booking_enabled=true`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.services = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingService = false
        })
    },
    fetchProviders (service) {
      this.isFetchingProvider = true
      this.providers = []
      this.unavailableDates = []
      this.times = []
      !this.form.provider || this.$set(this.form, 'provider', null)
      !this.form.date || this.$set(this.form, 'date', null)
      !this.form.time || this.$set(this.form, 'time', null)
      this.selectedDate = null
      this.selectedDateField = null
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/providers?page_size=-1&services=${service}&status=active&fields=id,name`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.providers = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingProvider = false
        })
    },
    fetchUnavailableDates (yearMonth) {
      this.isFetchingDates = true
      let params = {
        services: this.form.service,
        provider: this.form.provider
      }
      if (yearMonth) {
        params.year = yearMonth.substr(0, 4),
        params.month = yearMonth.substr(-2)
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/block_dates`, { params })
        .then(resp => {
          this.unavailableDates = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingDates = false
        })
    },
    fetchTimes (service, provider, date) {
      this.isFetchingTimes = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/providers/${provider}/available_times?service=${service}&date=${date}`)
        .then(resp => {
          this.times = resp.data.times.map(m => { return { text: this.$options.filters.displayTime2(m), value: m }})
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingTimes = false
        })
    },
    unavailableDateList (val) {
      if (this.unavailableDates.includes(val)) {
        return false
      }
      return true
    },
    clearDateTime () {
      this.times = []
      !this.form.date || this.$set(this.form, 'date', null)
      !this.form.time || this.$set(this.form, 'time', null)
      this.selectedDate = null
      this.selectedDateField = null
      this.fetchUnavailableDates(this.pickerDate)
    },
    pickDate (service, provider, date) {
      this.selectedDateField = this.$options.filters.humanizeDate(date)
      this.$set(this.form, 'date', date)
      this.showPicker = false
      !this.form.time || this.$set(this.form, 'time', null)
      this.fetchTimes(service, provider, date)
    },
    confirmAction () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      let data = {
        service: this.form.service,
        provider: this.form.provider,
        date: this.form.date,
        time: this.form.time,
        patient: this.appointment.patient.id
      }
      if (this.form.remarks) {
        data.lead = { remarks: this.form.remarks }
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      const endpoint = `/clinics/${companySlug}/appointments`
      this.$axios.post(endpoint, data)
        .then(resp => {
          this.updateActivityLog(endpoint, data, resp, 'POST')
          this.closeResetDialog()
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeDate () {
      this.showPicker = false
    },
    closeResetDialog () {
      this.showDialog = false
      this.$emit('reset')
    },
    closeDialog () {
      this.showDialog = false
      this.$emit('closed')
    },
    getTodayDate () {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    openAdvance () {
      this.$emit('advance', this.appointment.patient)
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: 'Create followup appointment',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>